import { Button, Dialog, Note } from "@appretio/ui-kit";
import type {
  DraftSummaryLine,
  UnsavedChangesDialogState
} from "../hooks/useUnsavedChangesGuard";
import * as styles from "./UnsavedChangesDialog.css";

/**
 * Props for the unsaved-changes summary dialog. The state half comes
 * from ``useUnsavedChangesGuard().dialogProps`` (spread it on); the
 * label half is per call site — e.g. range-plan's Apply passes
 * ``description="Applying a new context discards your draft:"`` with
 * "Save & apply" / "Discard & apply" buttons.
 */
export interface UnsavedChangesDialogProps extends UnsavedChangesDialogState {
  /** Dialog title. Defaults to "Unsaved changes". */
  title?: string;
  /** Consequence line shown as the warning note, e.g. "Applying a new context discards your draft:". Overridable per guarded action via ``guard()``'s ``labels``. */
  description: string;
  /** Save-first button label. Defaults to "Save & continue". Overridable per guarded action. */
  saveLabel?: string;
  /** Discard button label. Defaults to "Discard & continue". Overridable per guarded action. */
  discardLabel?: string;
  /** Cancel button label. Defaults to "Cancel". */
  cancelLabel?: string;
}

function summaryLineText(line: DraftSummaryLine): string {
  return line.count !== undefined ? `${line.count} ${line.label}` : line.label;
}

/**
 * Blocking modal that intercepts a guarded action while the module's
 * draft is dirty (design A — summary modal): a warning line, the
 * itemised draft summary, and [save-first] [discard] [Cancel] actions.
 * Cancel (button, Esc, outside click, ×) keeps the draft and drops the
 * pending action. Mid-save every dismissal path is locked so a slow
 * save can't be double-triggered or abandoned half-way.
 */
export function UnsavedChangesDialog({
  open,
  summary,
  labels,
  isSaving,
  saveError,
  canSaveFirst,
  onSaveAndContinue,
  onDiscardAndContinue,
  onCancel,
  title = "Unsaved changes",
  description,
  saveLabel = "Save & continue",
  discardLabel = "Discard & continue",
  cancelLabel = "Cancel"
}: UnsavedChangesDialogProps) {
  // Unmount entirely while closed so a stale summary never flashes on
  // the next open (state is re-captured at guard time).
  if (!open) return null;

  return (
    <Dialog
      hideFeedbackIcon
      open
      title={title}
      size="md"
      // Dialog's isLoading gates Esc / outside-click / × while the
      // save-first flow is in flight — the pending action must resolve
      // through one of the three buttons once a save has started.
      isLoading={isSaving}
      onOpenChange={function handleOpenChange(nextOpen: boolean) {
        if (!nextOpen) onCancel();
      }}
      onClose={onCancel}
    >
      <div className={styles.body}>
        <Note variant="warning" text={labels?.description ?? description} />
        {summary.length > 0 && (
          <ul className={styles.summaryList}>
            {summary.map((line) => (
              <li key={line.label} className={styles.summaryItem}>
                {summaryLineText(line)}
              </li>
            ))}
          </ul>
        )}
        {saveError !== null && (
          <Note variant="error" size="compact" text={saveError} />
        )}
        <footer className={styles.footer}>
          {canSaveFirst && (
            <Button
              type="button"
              variant="primary"
              isLoading={isSaving}
              onClick={onSaveAndContinue}
            >
              {labels?.saveLabel ?? saveLabel}
            </Button>
          )}
          <Button
            type="button"
            variant="secondary"
            disabled={isSaving}
            onClick={onDiscardAndContinue}
          >
            {labels?.discardLabel ?? discardLabel}
          </Button>
          <Button
            type="button"
            variant="tertiary"
            disabled={isSaving}
            onClick={onCancel}
          >
            {cancelLabel}
          </Button>
        </footer>
      </div>
    </Dialog>
  );
}
